import { App } from 'antd'
import React, { useCallback } from 'react'

import { NormalStatus } from '@/constants/normal-status'
import useFormModal from '@/hooks/use-form-modal'
import { Role } from '@/model/role'
import { createRole, updateRole } from '@/redux/role'
import { useAppDispatch } from '@/redux/utils'

import { RoleEditFormFields } from '../components/role-edit-form-fields'

interface RoleEditFormValues {
  id?: number;
  name: string;
  description: string;
}

export default function useRoleEditModal({
  roles,
  onCreateOrUpdate,
}: {
  roles: Role[];
  onCreateOrUpdate: (role: Role, created: boolean) => void;
}) {
  const dispatch = useAppDispatch()
  const { message } = App.useApp()

  const onUpsertRole = useCallback(
    async (values: RoleEditFormValues) => {
      const role = roles.find((role) => role.id === values.id)
      if (!role) return
      const result = await dispatch(
        updateRole({
          id: role.id,
          name: values.name,
          description: values.description,
        }),
      ).unwrap()
      onCreateOrUpdate(result, false)
      void message.success('Role Updated')
    },
    [dispatch, roles, message, onCreateOrUpdate],
  )

  const onCreateRole = useCallback(
    async (values: RoleEditFormValues) => {
      const role = await dispatch(
        createRole({
          name: values.name,
          description: values.description,
          status: NormalStatus.Activated,
        }),
      ).unwrap()
      onCreateOrUpdate(role, true)
      void message.success('Role Created')
    },
    [dispatch, message, onCreateOrUpdate],
  )

  const [updateFormModal, openUpdateModal] = useFormModal<RoleEditFormValues>({
    onSubmit: onUpsertRole,
    getFields: () => <RoleEditFormFields />,
    title: 'Edit Role',
  })

  const [createFormModal, openCreateModal] = useFormModal<RoleEditFormValues>({
    onSubmit: onCreateRole,
    getFields: () => <RoleEditFormFields />,
    title: 'Add Role',
  })

  return { createFormModal, openCreateModal, updateFormModal, openUpdateModal }
}
